import React, {
  useState,
  Fragment,
  useRef,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { FieldArray } from 'formik';
import styled from 'styled-components';
import TextFieldPOS from '../../components/TextFieldPOS';
import ToogleFieldPOS from '../../components/ToogleFieldPOS';
import CustomePagination from '../../../../../components/Pagination/CustomePagination';
import CreatableSelect from '../../../../../components/Select/CreatableSelect';
import StickyButtons from '../../../../../components/StickyButtons';
import ConfirmUnsavedChangesModal from '../../components/ConfirmUnsavedChangesModal';
import { discountOptionsModified } from '../../utils/utils';
import {
  MainDiv,
  PaginationWrapper,
  Body,
  DiscountDiv,
  DiscountNameCell,
  POSDiv,
  DiscountHeader,
  POSHeader,
  POSFieldWrapper,
  POSValueCell,
  LineDivider,
  ToogleFieldPOSWrapper,
  SelectDiv,
} from '../../styles/styles';
import { CustomizationContext } from '../../../../../utils/ClientCustomizationsProvider';
import { PrimaryButton } from '../../../Products/ProductWizard/components/ProductPreview/components/Buttons';
import CouponOverridesModal from './CouponOverridesModal';
import { connect } from 'react-redux';
import { submitPOSCouponOverrides } from '../../posAction';

const ContainedButton = styled(PrimaryButton)`
  height: 45px;
  border-radius: 20px;
  font-size: 12px;
`;

const CouponsMappingTable = ({
  meta,
  requestParams,
  updateRequestParams,
  dirty,
  handleReset,
  values,
  initialValues,
  coupons,
  getCoupons,
  type,
  submitPOSDiscountValuesCoupons,
  initialRequestParams,
  selectedValuePOS,
  allCoupons,
  posDiscountsMappingFields,
  posDiscountsFieldsNum,
  businessList,
  submitPOSCouponOverrides,
}) => {
  const { isOfferOverridesClient } = useContext(CustomizationContext);
  const searchRef = useRef();
  const modifiedType = type === 'credit code' ? 'coupon' : type;
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const discountOptions = discountOptionsModified(allCoupons, type);
  const [newValue, setNewValue] = useState(null);
  const [newSearch, setNewSearch] = useState(null);
  const [newPage, setNewPage] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchString, setSearchString] = useState('');
  const [isCouponOverrideModalOpen, setIsCouponOverrideModalOpen] =
    useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCouponOverride, setSelectedCouponOverride] = useState(null);

  const handleChange = (newValue) => {
    searchRef.current = newValue;
    const searchString =
      typeof newValue === 'string'
        ? newValue
        : newValue?.inputValue || newValue?.value || '';
    setSearchString(searchString);

    if (!searchString) {
      setNewValue(null);
      getCoupons(initialRequestParams);
      return;
    }
    setPage(1);
    if (dirty) {
      setNewValue(newValue);
      setIsConfirmModal(true);
    } else {
      getCoupons(
        {
          searchString,
          page_number: 1,
          page_size: rowsPerPage,
        },
        true
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const result = getCoupons({
        searchString: searchString,
        page_number: page,
        page_size: rowsPerPage,
      });
    };
    fetchData();
  }, [page, rowsPerPage]);

  const discountMappingWithOverrides = useMemo(() => {
    if (isOfferOverridesClient) {
      const newPosDiscountsMappingFields = { ...posDiscountsMappingFields };
      newPosDiscountsMappingFields['Overrides'] = {
        type: 'button',
        label: 'Overrides',
        optional: true,
      };
      return newPosDiscountsMappingFields;
    }
    return posDiscountsMappingFields;
  }, [posDiscountsMappingFields, isOfferOverridesClient]);

  const headerNumber = Object.keys(discountMappingWithOverrides).length;

  const handleClickOverride = (coupon) => {
    setSelectedCouponOverride(coupon);
    setIsCouponOverrideModalOpen(true);
  };

  const modifiedBusinessList = businessList?.map((business) => {
    return {
      id: business.id,
      name: business.name,
      display_name: business.display_name,
      business_name: business.business_name,
    };
  });

  const overriddenBusinessIds = useMemo(() => {
    if (!selectedCouponOverride?.pos_mappings) return [];
    return (
      selectedCouponOverride?.pos_mappings
        ?.filter((mapping) => mapping?.business_id)
        .map((mapping) => mapping.business_id) || []
    );
  }, [selectedCouponOverride]);

  const handleSubmitCouponOverrides = async (values, method) => {
    const { businessIds, ...restValues } = values;
    await submitPOSCouponOverrides(restValues, businessIds, method);
  };

  const handleCouponOverridesBatchComplete = async () => {
    await getCoupons(initialRequestParams);
    setIsCouponOverrideModalOpen(false);
  };

  return (
    <MainDiv>
      <SelectDiv>
        <CreatableSelect
          onChange={handleChange}
          options={discountOptions || []}
          value={newValue}
          placeholder='Search for credit codes'
          isClearable={!dirty}
        />
      </SelectDiv>
      <Body>
        <DiscountDiv>
          <DiscountHeader>
            <div>{type.charAt(0).toUpperCase() + type.slice(1)} Name</div>
          </DiscountHeader>
          <LineDivider />
          {coupons.map((coupon, index) => {
            return (
              <div key={coupon.coupon_details.coupon_id}>
                <DiscountNameCell>
                  {coupons[index].coupon_details.label}
                </DiscountNameCell>
              </div>
            );
          })}
        </DiscountDiv>

        <POSDiv>
          <POSFieldWrapper fieldsNumber={headerNumber}>
            {Object.keys(discountMappingWithOverrides).map((item, index) => {
              return (
                <POSHeader key={index}>
                  {item}{' '}
                  {discountMappingWithOverrides[item].optional ? '' : '*'}
                </POSHeader>
              );
            })}
            <LineDivider fieldsNumber={headerNumber} />

            <FieldArray
              name='coupons'
              render={(arrayHelpers) => (
                <Fragment>
                  {coupons.map((coupon) => {
                    return (
                      <Fragment key={`c_${coupon.coupon_details.coupon_id}`}>
                        {Object.keys(discountMappingWithOverrides).map(
                          (key) => {
                            return (
                              <POSValueCell
                                key={`p_${coupon.coupon_details.coupon_id}_${key}`}
                              >
                                {discountMappingWithOverrides[key].type ===
                                'string' ? (
                                  <TextFieldPOS
                                    name={`coupons[${coupon.index}].pos_mapping['${key}']`}
                                    value={
                                      coupon?.pos_mapping
                                        ? coupon?.pos_mapping[key]
                                        : ''
                                    }
                                    setFieldValue={
                                      arrayHelpers.form.setFieldValue
                                    }
                                  />
                                ) : discountMappingWithOverrides[key].type ===
                                  'button' ? (
                                  <ContainedButton
                                    buttonWidth={110}
                                    onClick={() => handleClickOverride(coupon)}
                                  >
                                    Override
                                  </ContainedButton>
                                ) : (
                                  <ToogleFieldPOSWrapper>
                                    <ToogleFieldPOS
                                      name={`coupons[${coupon.index}].pos_mapping['${key}']`}
                                      onChange={
                                        arrayHelpers.form.setFieldValue
                                      }
                                    />
                                  </ToogleFieldPOSWrapper>
                                )}
                              </POSValueCell>
                            );
                          },
                        )}
                      </Fragment>
                    );
                  })}
                  <StickyButtons
                    isVisible={dirty}
                    handleDiscard={() => handleReset()}
                    handleSave={() => {
                      submitPOSDiscountValuesCoupons(
                        values,
                        modifiedType,
                        initialValues,
                        selectedValuePOS,
                        null,
                        null,
                        meta.page_size,
                        meta.page_number,
                        posDiscountsMappingFields
                      );
                      setNewValue('');
                    }}
                    title='Credit Codes POS Mapping'
                  />
                </Fragment>
              )}
            />
          </POSFieldWrapper>
        </POSDiv>
      </Body>

      <PaginationWrapper>
        <CustomePagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          hasNextPage={meta.has_next_page}
          disabled={dirty}
        />
      </PaginationWrapper>
      {isCouponOverrideModalOpen && (
        <CouponOverridesModal
          isOverrideModal={isCouponOverrideModalOpen}
          setIsOverrideModal={setIsCouponOverrideModalOpen}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          modifiedBusinessList={modifiedBusinessList}
          posDiscountsMappingFields={posDiscountsMappingFields}
          selectedCouponOverride={selectedCouponOverride}
          onSubmit={handleSubmitCouponOverrides}
          onBatchComplete={handleCouponOverridesBatchComplete}
          overriddenBusinessIds={overriddenBusinessIds}
        />
      )}
      {isConfirmModal && (
        <ConfirmUnsavedChangesModal
          toggleConfirmModal={() => setIsConfirmModal(false)}
          handleSave={() => {
            setIsConfirmModal(false);
            submitPOSDiscountValuesCoupons(
              values,
              modifiedType,
              initialValues,
              selectedValuePOS,
              newValue,
              newSearch,
              meta.page_size,
              newPage ? newPage : meta.page_number,
              posDiscountsMappingFields
            );
            setNewSearch(null);
            setNewPage(null);
          }}
        />
      )}
    </MainDiv>
  );
};

const mapStateToProps = (state) => ({
  businessList: state.businessLocationsList.businessLocationsOfMerchant,
});

export default connect(mapStateToProps, { submitPOSCouponOverrides })(
  CouponsMappingTable,
);
